import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { autoUpdate, flip, offset, safePolygon, useClick, useDismiss, useFloating, useHover, useInteractions, useTransitionStyles } from '@floating-ui/react';

import { hasRecentMouseMove, startPointerActivityTracking } from '../../lib/pointerActivity';

import { KEYCHAIN, useAnyKeychainsRegistered } from '../../contexts/KeyMaster';
import DarkTooltipPortal from './DarkTooltipPortal';

// Context to allow parent components to override the z-index class for popovers
// This is needed because agent chat UI has higher z-indexes than the default
export const PopoverZIndexContext = createContext('z-(--stack-context-menu)');

// How fresh mouse movement must be for hover open/close to count.
const HOVER_MOVE_GRACE_MS = 1200;

// Avoid entity popovers when context menus or certain modals are open.
const ENTITY_POPOVER_SUPPRESS_KEYCHAINS = [
  KEYCHAIN.MODAL__CONFERENCE_TRANSCRIPT,
  KEYCHAIN.POPOVER__CONTEXT_MENU,
  KEYCHAIN.POPOVER__SIDEBAR_CONTEXT_MENU,
  KEYCHAIN.POPOVER__SIDEBAR_SECTION_CONTEXT_MENU,
];

// Shared tooltip for entity popups
const EntityPopover = ({ children, content, interactive = true, hoverEnabled = true, clickEnabled = false }) => {
  const zIndexClass = useContext(PopoverZIndexContext);
  const [isOpen, setIsOpen] = useState(false);
  const isSuppressed = useAnyKeychainsRegistered(ENTITY_POPOVER_SUPPRESS_KEYCHAINS);
  // True when a close was ignored due to layout shift + stationary mouse.
  const pendingStaleCloseRef = useRef(false);

  useEffect(() => {
    // Start tracking early so first hover after load is captured.
    startPointerActivityTracking();
  }, []);

  const handleOpenChange = (nextOpen, _event, reason) => {
    if (isSuppressed) {
      pendingStaleCloseRef.current = false;
      setIsOpen(false);
      return;
    }

    // Only gate hover-driven changes. Let non-hover paths behave normally.
    // Ignore hover toggles caused by layout shifts under a stationary mouse.
    if ((reason === 'hover' || reason === 'safe-polygon') && !hasRecentMouseMove(HOVER_MOVE_GRACE_MS)) {
      // If we suppressed a close, settle it on the next real mouse move.
      if (!nextOpen) pendingStaleCloseRef.current = true;
      return;
    }

    pendingStaleCloseRef.current = false;
    setIsOpen(nextOpen);
  };

  useEffect(() => {
    if (!isSuppressed) return;
    pendingStaleCloseRef.current = false;
    setIsOpen(false);
  }, [isSuppressed]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: 'top',
    // Flip is needed so it can tell if we're too close to the top, and shift us to align bottom instead
    // Offset just gives us some additioanl vertical space, to match Figma
    middleware: [offset(20), flip()],
    // This is important, makes it smart enough to measure the tooltips position interms of the virtual scroller's full height
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!isOpen) {
      pendingStaleCloseRef.current = false;
      return;
    }

    const handleMouseMove = (event) => {
      // No deferred close pending, nothing to do.
      if (!pendingStaleCloseRef.current) return;
      if (!(event.target instanceof Node)) return;

      const referenceEl = refs.reference.current;
      const floatingEl = refs.floating.current;
      const isOverReference = !!referenceEl && referenceEl.contains(event.target);
      const isOverFloating = !!floatingEl && floatingEl.contains(event.target);

      // User moved again; if they're not on trigger/popover anymore, close it.
      if (!isOverReference && !isOverFloating) {
        pendingStaleCloseRef.current = false;
        setIsOpen(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isOpen, refs]);

  // Same entrance as the shared Tooltip: scale up out of the anchor's edge, quick fade on close.
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 180, close: 100 },
    initial: { opacity: 0, transform: 'scale(0.85)' },
    common: ({ side }) => ({
      transformOrigin: { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[side],
      transitionTimingFunction: 'var(--ease-fluid)',
    }),
  });

  const hover = useHover(context, {
    enabled: hoverEnabled,
    // Number of milliseconds to wait before opening the tooltip
    // This is to prevent the tooltip from opening too quickly when the user is mousing around the page
    delay: { open: 250 },
    handleClose: safePolygon({
      // This is so you can hover up to the tooltip over another thing that would trigger a tooltip (e.g, rows of entities),
      // and that hover gap up to the tooltip doesn't trigger the tooltip of the element above the one you originally hovered.
      blockPointerEvents: true,
    }),
  });
  // toggle: false so a click on the trigger never dismisses a popover the hover just opened.
  const click = useClick(context, {
    enabled: clickEnabled,
    toggle: false,
  });
  const dismiss = useDismiss(context, {
    enabled: clickEnabled,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, dismiss]);

  return (
    <>
      {React.cloneElement(children, {
        ref: refs.setReference,
        // Passing the child's props lets floating-ui compose its handlers with ours instead of clobbering them.
        ...getReferenceProps(children.props),
      })}

      {isMounted && interactive && content && !isSuppressed && (
        <DarkTooltipPortal
          variant="entity-popover"
          setFloating={refs.setFloating}
          floatingStyles={floatingStyles}
          floatingProps={getFloatingProps()}
          zIndexClass={zIndexClass}
          transitionStyles={transitionStyles}
        >
          {content}
        </DarkTooltipPortal>
      )}
    </>
  );
};

export default EntityPopover;
